import { Fragment, useState } from "react";
import type { CodeCaseQuery, OrgDashboard } from "../types";
import { CodeTableToolbar, fieldLabel, useCodeFilter } from "./codeTableToolbar";

function pct(rate: number): string {
  return `${(rate * 100).toFixed(1)}%`;
}

/** How often each individual code was carried by both sides.
 *
 *  Rows arrive ranked by the server and are NOT re-sorted here, for the same
 *  reason the errored-code table is not: a second sort is a second place for
 *  ties to resolve differently between renders. */
export function CorpusCodeTable({
  codes,
  onOpenCase,
  onOpenCodeCases,
}: {
  codes: OrgDashboard["corpus"]["codes"];
  onOpenCase?: (dcn: string) => void;
  onOpenCodeCases?: (q: CodeCaseQuery) => void;
}) {
  const filter = useCodeFilter(codes);
  const [expanded, setExpanded] = useState<string | null>(null);

  return (
    <div className="flex flex-col gap-2.5">
      <CodeTableToolbar
        categories={filter.categories}
        counts={filter.counts}
        total={filter.total}
        category={filter.category}
        onCategory={filter.setCategory}
        search={filter.search}
        onSearch={filter.setSearch}
      />

      <div className="max-h-[420px] overflow-auto rounded-coronis border border-border">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10">
            <tr className="border-b border-border bg-muted text-left text-[11px] font-medium uppercase tracking-[0.04em] text-muted-foreground">
              <th className="py-2 pl-4 pr-3 font-medium">Code</th>
              <th className="px-3 py-2 font-medium">Field</th>
              <th className="px-3 py-2 text-right font-medium">Seen</th>
              <th className="px-3 py-2 text-right font-medium">Matched</th>
              <th className="px-3 py-2 pr-4 text-right font-medium">
                Match rate
              </th>
            </tr>
          </thead>
          <tbody>
            {filter.shown.map((c) => {
              const key = `${c.field}:${c.code}`;
              const canDrill = !!onOpenCase && c.exampleDcns.length > 0;
              const open = expanded === key;
              return (
                <Fragment key={key}>
                  <tr
                    onClick={canDrill ? () => setExpanded(open ? null : key) : undefined}
                    className={
                      "border-b border-border last:border-0 " +
                      (canDrill ? "cursor-pointer hover:bg-muted/50" : "")
                    }
                  >
                    <td className="py-2.5 pl-4 pr-3 font-medium tabular-nums text-foreground">
                      {c.code}
                      {canDrill && (
                        <span className="ml-1.5 text-[11px] font-normal text-muted-foreground">
                          {open ? "▾" : "▸"}
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-muted-foreground">
                      {fieldLabel(c.field)}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-foreground">
                      {c.seen.toLocaleString()}
                    </td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-foreground">
                      {c.matched.toLocaleString()}
                    </td>
                    <td className="px-3 py-2.5 pr-4 text-right font-semibold tabular-nums text-foreground">
                      {c.seen > 0 ? pct(c.matched / c.seen) : "—"}
                    </td>
                  </tr>
                  {open && (
                    <tr className="border-b border-border last:border-0 bg-muted/30">
                      <td colSpan={5} className="px-4 py-2.5">
                        <div className="flex flex-wrap items-center gap-1.5">
                          <span className="text-[11.5px] text-muted-foreground">
                            Examples:
                          </span>
                          {c.exampleDcns.map((dcn) => (
                            <button
                              key={dcn}
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                onOpenCase?.(dcn);
                              }}
                              className="rounded-coronis border border-border bg-card px-2 py-0.5 text-[12px] font-medium tabular-nums text-foreground transition-colors hover:bg-muted"
                            >
                              {dcn}
                            </button>
                          ))}
                          {/* Says WHY the list is short, so a reader does not
                              read five as the code's total. */}
                          <span className="text-[11.5px] text-muted-foreground">
                            a few of {c.seen.toLocaleString()}
                          </span>
                          {canDrill && onOpenCodeCases && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                onOpenCodeCases({ mode: "corpus", field: c.field, code: c.code });
                              }}
                              className="text-[12.5px] font-medium text-coronis-blue-600 hover:underline"
                            >
                              View all cases →
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
